import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts'
import ChartContainer from './ChartContainer'
import { RechartsChartTooltipProvider, StableRechartsChartTooltip } from './ChartTooltip'
import { defaultFormatter } from './formatters'

const BasePieChart = ({
  data,
  xKey,
  series,
  height = 200,
  loading,
  emptyMessage,
  valueFormatter,
  labelFormatter,
  showLegend = false,
  showTooltip = false,
  showLabels = false,
  sleek = false,
  donut = true,
  innerRadius = 50,
  outerRadius = 70,
  labelOffset = 22,
  labelLineProps,
  labelMinValue = 0.5,
  onSliceClick,
  activeKey,
  valueKey = 'value',
  chartMargin,
}) => {
  const pieRows = (data || []).filter((row) => Number(row?.[valueKey]) > 0)
  const empty = pieRows.length === 0
  const normalizedSeries = (series || []).map((item) => ({
    ...item,
    formatter: item.formatter || valueFormatter || defaultFormatter,
  }))

  const pieMargin =
    chartMargin ??
    (showLabels ? { top: 28, right: 72, bottom: 44, left: 72 } : undefined)

  return (
    <ChartContainer
      height={height}
      loading={loading}
      empty={empty}
      emptyMessage={emptyMessage}
      className={showLabels ? 'rg-chart--pie-labels' : ''}
    >
      <RechartsChartTooltipProvider series={normalizedSeries} options={{ valueFormatter, labelFormatter }}>
        <PieChart margin={pieMargin}>
          {showTooltip && <Tooltip content={StableRechartsChartTooltip} />}
        {showLegend && <Legend verticalAlign="bottom" align="center" iconType="circle" className="rg-chart__legend" />}
        <Pie
          data={pieRows}
          dataKey={valueKey}
          nameKey={xKey}
          innerRadius={donut ? innerRadius : 0}
          outerRadius={outerRadius}
          paddingAngle={sleek ? 1 : 2}
          cornerRadius={sleek ? 6 : 0}
          stroke={sleek ? 'var(--chart-surface)' : undefined}
          strokeWidth={sleek ? 2 : 0}
          label={showLabels ? ({ cx, cy, midAngle, outerRadius: radius, payload, value }) => {
            const numericValue = Number(value) || 0
            if (numericValue < labelMinValue) return null

            const RADIAN = Math.PI / 180
            const config = normalizedSeries.find((item) => item.key === payload?.[xKey])
            const formatter = config?.formatter || valueFormatter || defaultFormatter
            const colorVar = config?.colorVar || '--chart-1'
            const stroke = labelLineProps?.stroke || `var(${colorVar})`
            const strokeWidth = labelLineProps?.strokeWidth ?? 1
            const lineOffset = 12
            const sx = cx + radius * Math.cos(-midAngle * RADIAN)
            const sy = cy + radius * Math.sin(-midAngle * RADIAN)
            const mx = cx + (radius + labelOffset) * Math.cos(-midAngle * RADIAN)
            const my = cy + (radius + labelOffset) * Math.sin(-midAngle * RADIAN)
            const isRight = Math.cos(-midAngle * RADIAN) >= 0
            const ex = mx + (isRight ? lineOffset : -lineOffset)
            const ey = my
            const labelX = ex + (isRight ? 6 : -6)
            return (
              <g>
                <path
                  d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  {...(labelLineProps || {})}
                />
                <text
                  x={labelX}
                  y={ey}
                  fill="var(--chart-muted)"
                  fontSize={11}
                  textAnchor={isRight ? 'start' : 'end'}
                  dominantBaseline="central"
                >
                  {formatter(value)}
                </text>
              </g>
            )
          } : false}
          labelLine={false}
        >
          {pieRows.map((entry) => {
            const config = normalizedSeries.find((item) => item.key === entry[xKey])
            const colorVar = config?.colorVar || '--chart-1'
            return (
              <Cell
                key={entry[xKey]}
                fill={`var(${colorVar})`}
                onClick={onSliceClick ? () => onSliceClick(entry[xKey]) : undefined}
                style={{
                  cursor: onSliceClick ? 'pointer' : 'default',
                  opacity: !activeKey || activeKey === 'all' || activeKey === entry[xKey] ? 1 : 0.3,
                }}
              />
            )
          })}
        </Pie>
      </PieChart>
      </RechartsChartTooltipProvider>
    </ChartContainer>
  )
}

export default BasePieChart
